/**
 * Registro región→endpoint (TAREA 3: hosting regional real).
 *
 * FUENTE ÚNICA de "qué servidor hostea cada región". Antes había un único
 * `VITE_NETPLAY_URL` y la región era solo una etiqueta; ahora cada `RegionId`
 * puede apuntar a su propio `wss://…` (un edge desplegado por región), y el
 * cliente elige a cuál conectarse por ping/elección del usuario.
 *
 * Configuración (build-time, Netlify env):
 *   - `VITE_REGION_ENDPOINTS` = JSON `{"us-east":"wss://…","eu-west":"wss://…"}`
 *   - o, compat de 1 región, `VITE_NETPLAY_URL` = "wss://…" (se mapea a
 *     `VITE_DEFAULT_REGION`, por defecto "us-east").
 *
 * El parser es puro (testeable sin DOM); el wiring a `import.meta.env` va al final.
 */
import { RegionId } from "./contract";

const VALID_REGIONS: readonly RegionId[] = [
  "us-east", "us-west", "eu-west", "eu-central", "sa-east", "ap-south", "ap-southeast",
];

/**
 * `RegionId` → código de región de Fly.io.
 *
 * Fly es el proveedor elegido para el hosting regional porque tiene edges en
 * sitios donde los demás no llegan — `gru` (São Paulo) y `bom` (Bombay) son la
 * diferencia entre 40 ms y 200 ms para media base de jugadores, y ese salto no
 * se compensa con nada del lado del netcode.
 *
 * El mapa vive AQUÍ, junto al resto del registro de regiones, y no en un script
 * de despliegue: `RegionId` es lo que el juego entiende y lo que viaja en la
 * lista de salas, así que la traducción a códigos de proveedor tiene que ser
 * una sola y estar donde se pueda comprobar. Cambiar de proveedor es reescribir
 * esta tabla, no perseguir códigos sueltos por el repo.
 */
export const FLY_REGIONS: Record<RegionId, string> = {
  "us-east": "iad",       // Ashburn, Virginia
  "us-west": "sjc",       // San José, California
  "eu-west": "lhr",       // Londres
  "eu-central": "fra",    // Fráncfort
  "sa-east": "gru",       // São Paulo
  "ap-south": "bom",      // Bombay
  "ap-southeast": "sin",  // Singapur
};

/** Código de Fly de una región (o `undefined` si no es una región conocida). */
export function flyRegion(region: string): string | undefined {
  return isRegionId(region) ? FLY_REGIONS[region] : undefined;
}

/** `RegionId` que corresponde a un código de Fly (búsqueda inversa). */
export function regionFromFly(code: string): RegionId | undefined {
  const found = (Object.entries(FLY_REGIONS) as [RegionId, string][])
    .find(([, fly]) => fly === code.toLowerCase());
  return found?.[0];
}

export interface RegionRegistry {
  /** RegionId → URL WebSocket del edge que la hostea (wss:// o ws://). */
  endpoints: Partial<Record<RegionId, string>>;
  /** Región elegida por defecto (primera configurada o la de `VITE_DEFAULT_REGION`). */
  defaultRegion: RegionId;
}

function isRegionId(v: string): v is RegionId {
  return (VALID_REGIONS as readonly string[]).includes(v);
}

/** Normaliza un endpoint a esquema WebSocket (acepta http(s):// y ws(s)://). */
function toWs(url: string): string | null {
  const u = url.trim();
  if (/^wss?:\/\//i.test(u)) return u;
  if (/^https:\/\//i.test(u)) return u.replace(/^https/i, "wss");
  if (/^http:\/\//i.test(u)) return u.replace(/^http/i, "ws");
  return null;
}

/**
 * Construye el registro a partir de la config de build. Puro y sin DOM.
 * @param endpointsJson valor de `VITE_REGION_ENDPOINTS` (JSON) o undefined.
 * @param fallbackUrl   valor de `VITE_NETPLAY_URL` (1 región) o undefined.
 * @param defaultRegionEnv valor de `VITE_DEFAULT_REGION` o undefined.
 */
export function parseRegionRegistry(
  endpointsJson?: string,
  fallbackUrl?: string,
  defaultRegionEnv?: string,
): RegionRegistry {
  const endpoints: Partial<Record<RegionId, string>> = {};

  if (endpointsJson && endpointsJson.trim()) {
    try {
      const obj = JSON.parse(endpointsJson) as Record<string, unknown>;
      for (const [k, v] of Object.entries(obj)) {
        if (!isRegionId(k) || typeof v !== "string") continue;
        const ws = toWs(v);
        if (ws) endpoints[k] = ws;
      }
    } catch {
      /* JSON inválido → se ignora y se cae al fallback */
    }
  }

  const envDefault = defaultRegionEnv && isRegionId(defaultRegionEnv) ? defaultRegionEnv : "us-east";

  // Compat de 1 región: si no hay mapa pero sí VITE_NETPLAY_URL, la asignamos a
  // la región por defecto (comportamiento idéntico al de antes de TAREA 3).
  if (Object.keys(endpoints).length === 0 && fallbackUrl) {
    const ws = toWs(fallbackUrl);
    if (ws) endpoints[envDefault] = ws;
  }

  const configured = Object.keys(endpoints) as RegionId[];
  const defaultRegion = endpoints[envDefault] ? envDefault : (configured[0] ?? envDefault);
  return { endpoints, defaultRegion };
}

/** URL HTTP base (para `/rooms` y `/ping`) derivada del endpoint WS de una región. */
export function wsToHttp(wsUrl: string): string {
  return wsUrl.replace(/^ws/i, "http").replace(/\/+$/, "");
}

/* --------------------------- wiring a import.meta.env --------------------- */
const ENV = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
export const REGISTRY: RegionRegistry = parseRegionRegistry(
  ENV.VITE_REGION_ENDPOINTS,
  ENV.VITE_NETPLAY_URL,
  ENV.VITE_DEFAULT_REGION,
);

/** Regiones con endpoint configurado (para el selector del lobby). */
export function configuredRegions(): RegionId[] {
  return Object.keys(REGISTRY.endpoints) as RegionId[];
}

/** Endpoint WS de una región (o el de la región por defecto). */
export function regionWsUrl(region?: string): string | undefined {
  if (region && isRegionId(region) && REGISTRY.endpoints[region]) return REGISTRY.endpoints[region];
  return REGISTRY.endpoints[REGISTRY.defaultRegion];
}

/** Base HTTP de una región (para lista de salas y probe de ping). */
export function regionHttpBase(region?: string): string | undefined {
  const ws = regionWsUrl(region);
  return ws ? wsToHttp(ws) : undefined;
}

/** ¿Hay al menos un backend configurado? (si no, el lobby usa el mock offline). */
export function hasBackend(): boolean {
  return configuredRegions().length > 0;
}

/** Edges ya calentados en esta sesión (no tiene sentido repetirlo). */
const warmed = new Set<string>();

/**
 * Abre la conversación con los servidores ANTES de que el jugador pulse jugar.
 *
 * Lo que se paga al conectar por primera vez no es solo el WebSocket: es DNS +
 * TCP + TLS, y en un hosting que duerme los contenedores, además, despertar el
 * proceso. Todo eso puede sumar cientos de milisegundos —o segundos— de pantalla
 * muerta justo cuando el jugador ya ha dicho "quiero jugar".
 *
 * Con un `GET /ping` lanzado en cuanto se sabe que va a jugar, ese coste se
 * adelanta a un momento en el que no molesta: el navegador reutiliza después la
 * conexión ya establecida y el servidor está despierto. Es best-effort puro —
 * ningún fallo aquí afecta a nada.
 */
export function warmUpBackend(): void {
  if (typeof fetch !== "function") return;
  for (const region of configuredRegions()) {
    const base = regionHttpBase(region);
    if (!base || warmed.has(base)) continue;
    warmed.add(base);
    void fetch(base + "/ping", { cache: "no-store", mode: "cors" }).catch(() => { /* best-effort */ });
  }
}
